import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEqual, debounce } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import { FilterButton } from 'site-modules/shared/components/inventory/filters-button/filters-button';

import './quick-filters.scss';

const SCROLL_OFFSET = 200;

export class QuickFilters extends Component {
  static propTypes = {
    facets: InventoryEntities.Facets,
    suggestedFacets: InventoryEntities.Facets,
    withFiltersBtn: PropTypes.bool,
    onFiltersBtnClick: PropTypes.func.isRequired,
    className: PropTypes.string,
    filtersButtonClassName: PropTypes.string,
    listClassName: PropTypes.string,
    sticky: PropTypes.bool,
    activeFiltersCount: PropTypes.number,
    renderSuggestedFacetsComponent: PropTypes.func.isRequired,
  };

  static defaultProps = {
    facets: [],
    suggestedFacets: [],
    withFiltersBtn: false,
    className: '',
    listClassName: null,
    filtersButtonClassName: undefined,
    sticky: false,
    activeFiltersCount: 0,
  };

  state = {
    displayLeftOverlay: false,
    displayRightOverlay: false,
  };

  componentDidMount() {
    this.setOverlays();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.facets, prevProps.facets)) {
      // Update overlays and list scroll position after filters update
      this.scrollToTheListStart();
      this.setOverlays();
    }

    if (this.props.sticky && !prevProps.sticky) {
      this.setOverlays();
    }
  }

  setOverlays = () => {
    const element = this.filtersList.current;

    if (!element) return;

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const maxScrollLeft = scrollWidth - clientWidth;

    this.setState({
      displayLeftOverlay: scrollLeft > 0,
      displayRightOverlay: scrollLeft < maxScrollLeft,
    });
  };

  filtersList = React.createRef();

  scrollToTheListStart = () => {
    const element = this.filtersList.current;
    if (!element) return;
    element.scrollLeft = 0;
  };

  handleListScroll = () => {
    this.setOverlays();
  };

  debouncedScrollHandler = debounce(this.handleListScroll, 250);

  handleLeftBtnClick = () => {
    const element = this.filtersList.current;
    element?.scrollBy?.({ top: 0, left: -SCROLL_OFFSET, behavior: 'smooth' });
  };

  handleRightBtnClick = () => {
    const element = this.filtersList.current;
    element?.scrollBy?.({ top: 0, left: SCROLL_OFFSET, behavior: 'smooth' });
  };

  renderFiltersButton() {
    const { filtersButtonClassName, onFiltersBtnClick, activeFiltersCount } = this.props;

    return (
      <FilterButton
        classes={filtersButtonClassName}
        onFiltersBtnClick={onFiltersBtnClick}
        activeFiltersCount={activeFiltersCount}
      />
    );
  }

  render() {
    const { displayLeftOverlay, displayRightOverlay } = this.state;
    const { suggestedFacets, withFiltersBtn, className, listClassName, renderSuggestedFacetsComponent } = this.props;

    const suggestedFacetsToRender = suggestedFacets.reduce(
      (result, facet) => [...result, ...facet.values.map(value => value)],
      []
    );

    const hasQuickFilters = suggestedFacetsToRender.length;

    if (!withFiltersBtn && !hasQuickFilters) return null;

    const paddingClassNames = classnames({ 'pb-lg-0': !hasQuickFilters });

    return (
      <div className={classnames('quick-filters', className)}>
        <div
          className={classnames(
            'overlay right text-end d-flex align-items-center justify-content-end',
            paddingClassNames,
            { invisible: !displayRightOverlay }
          )}
        >
          <button
            type="button"
            className="scroll-btn ps-1 pe-0"
            aria-hidden
            tabIndex={-1}
            onClick={this.handleRightBtnClick}
            data-testid="right-arrow"
          >
            <span className="arrow size-16 text-primary icon-arrow-right4" aria-hidden />
          </button>
        </div>
        {displayLeftOverlay && (
          <div className={classnames('overlay left text-start d-flex align-items-center', paddingClassNames)}>
            <button
              type="button"
              className="scroll-btn pe-1 ps-0 "
              aria-hidden
              tabIndex={-1}
              onClick={this.handleLeftBtnClick}
              data-testid="left-arrow"
            >
              <span className="arrow size-16 text-primary icon-arrow-left4" aria-hidden />
            </button>
          </div>
        )}
        <div
          className={classnames('quick-filters-list d-flex flex-nowrap', listClassName, paddingClassNames)}
          ref={this.filtersList}
          onScroll={this.debouncedScrollHandler}
          data-testid="quick-filters-list"
        >
          {withFiltersBtn && this.renderFiltersButton()}
          {Boolean(hasQuickFilters) && renderSuggestedFacetsComponent(suggestedFacetsToRender)}
        </div>
      </div>
    );
  }
}
